import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import styles from '../../styles/validateCertificate.module.css'

const STATUS_LABELS = {
    vigente:  { label: 'Vigente',  cls: styles.badge_valid },
    generado: { label: 'Generado', cls: styles.badge_valid },
    revocado: { label: 'Revocado', cls: styles.badge_revoked },
    vencido:  { label: 'Vencido',  cls: styles.badge_expired },
    correcion:{ label: 'En corrección', cls: styles.badge_other },
}

function StatusBadge({ status }) {
    const s = STATUS_LABELS[status] ?? { label: status, cls: styles.badge_other }
    return <span className={`${styles.badge} ${s.cls}`}>{s.label}</span>
}

function Field({ label, value }) {
    return (
        <div className={styles.field}>
            <span className={styles.field_label}>{label}</span>
            <span className={styles.field_value}>{value || '—'}</span>
        </div>
    )
}

function formatDate(dateStr) {
    if (!dateStr) return '—'
    const d = new Date(dateStr)
    if (isNaN(d)) return dateStr
    return d.toLocaleDateString('es-CO', { year: 'numeric', month: 'long', day: 'numeric' })
}

function CertificatePreview({ url }) {
    const [expanded, setExpanded] = useState(false)

    if (!url) return null

    return (
        <div className={styles.preview_wrapper}>
            <div className={styles.preview_header}>
                <span className={styles.preview_title}>
                    <span className={styles.preview_icon}>📄</span>
                    Vista previa del certificado
                </span>
                {/* <div className={styles.preview_actions}>
                    <button
                        className={styles.preview_btn}
                        onClick={() => setExpanded(v => !v)}
                        title={expanded ? 'Reducir' : 'Ampliar'}
                    >
                        {expanded ? '⊟ Reducir' : '⊞ Ampliar'}
                    </button>
                    <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.preview_btn_download}
                        download
                    >
                        ⬇ Descargar PDF
                    </a>
                </div> */}
            </div>
            <div className={`${styles.preview_frame_container} ${expanded ? styles.preview_expanded : ''}`}>
                <iframe
                    src={`${url}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
                    className={styles.preview_frame}
                    title="Vista previa del certificado"
                />
            </div>
        </div>
    )
}

export default function ValidateCertificate() {
    const { hash } = useParams()
    const [status, setStatus] = useState('loading') // loading | found | error
    const [cert, setCert]     = useState(null)
    const [errMsg, setErrMsg] = useState('')

    useEffect(() => {
        if (!hash) {
            setStatus('error')
            setErrMsg('Hash de certificado no válido.')
            return
        }

        const url = `${process.env.REACT_APP_VALIDATE_CERTIFICATE_URL}/${hash}`

        fetch(url, { headers: { Accept: 'application/json' } })
            .then(async res => {
                const json = await res.json()
                if (!res.ok || json.error) {
                    throw new Error(json.message || 'Certificado no encontrado.')
                }
                return json
            })
            .then(json => {
                setCert(json.data)
                setStatus('found')
            })
            .catch(err => {
                setErrMsg(err.message)
                setStatus('error')
            })
    }, [hash])

    return (
        <div className={styles.page}>
            {/* ── Hero banner ─────────────────────────────────────────────── */}
            <div className={styles.hero}>
                <img
                    src="/images/logo_white.png"
                    alt="CECOL OCP"
                    className={styles.hero_logo}
                />
                <div className={styles.hero_text}>
                    <h1 className={styles.hero_title}>Validación de Certificado</h1>
                    <p className={styles.hero_subtitle}>
                        CECOL OCP — Certificación de Competencias Laborales
                    </p>
                </div>
            </div>

            {/* ── Card ────────────────────────────────────────────────────── */}
            <div className={styles.card}>
                {status === 'loading' && (
                    <div className={styles.center}>
                        <div className={styles.spinner} />
                        <span className={styles.error_msg}>Verificando certificado...</span>
                    </div>
                )}

                {status === 'error' && (
                    <div className={styles.center}>
                        <span className={styles.error_icon}>🔍</span>
                        <span className={styles.error_msg}>{errMsg}</span>
                    </div>
                )}

                {status === 'found' && cert && (
                    <>
                        {/* Número y estado */}
                        <div className={styles.status_row}>
                            <StatusBadge status={cert.estado} />
                            <span className={styles.cert_number}>
                                Certificado Nro: {cert.nro_certificado}
                            </span>
                        </div>

                        {/* Vista previa del PDF */}
                        {cert.estado === 'vigente' && (
                            <CertificatePreview url={cert.url_certificado} />
                        )}

                        {/* Datos del titular */}
                        <div className={styles.grid}>
                            <Field label="Nombres"               value={cert.nombre} />
                            <Field label="Apellidos"             value={cert.apellido} />
                            <Field label="Tipo de documento"     value={cert.tipo_documento} />
                            <Field label="Número de documento"   value={cert.numero_documento} />
                            <Field label="Esquema de certificación" value={cert.esquema} />
                        </div>

                        {/* Fechas */}
                        <div className={styles.dates_row}>
                            <Field
                                label="Fecha de expedición"
                                value={formatDate(cert.fecha_expedicion)}
                            />
                            <Field
                                label="Fecha de renovación"
                                value={formatDate(cert.fecha_renovacion)}
                            />
                            <Field
                                label="Fecha de vencimiento"
                                value={formatDate(cert.fecha_vencimiento)}
                            />
                        </div>
                    </>
                )}
            </div>
        </div>
    )
}
